import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NAV_GROUPS } from "@/lib/nav";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CornerDownLeft, Loader2, Search } from "lucide-react";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (term.trim().length < 2) {
      setGroups([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(term.trim())}`, {
          signal: controller.signal,
        });
        setGroups(data.groups || []);
      } catch {
        /* superseded or failed — keep the previous list */
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      clearTimeout(timer.current);
      controller.abort();
    };
  }, [term]);

  const pages = useMemo(() => {
    const all = NAV_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));
    const q = term.trim().toLowerCase();
    if (!q) return all.slice(0, 8);
    return all.filter((i) => i.label.toLowerCase().includes(q) || i.group.toLowerCase().includes(q));
  }, [term]);

  const go = useCallback(
    (route, search) => {
      setOpen(false);
      setTerm("");
      navigate(search ? `${route}?q=${encodeURIComponent(search)}` : route);
    },
    [navigate]
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger"
        className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background/60 text-muted-foreground hover:text-foreground hover:border-brand/40 transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="text-xs">Search anything</span>
        <kbd className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/10 bg-white/5">
          ⌘K
        </kbd>
      </button>
      <button
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger-mobile"
        aria-label="Search"
        className="sm:hidden h-10 w-10 rounded-md border border-input flex items-center justify-center text-muted-foreground"
      >
        <Search className="h-4 w-4" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Jump to a ledger, invoice, vendor, FD…"
          value={term}
          onValueChange={setTerm}
          data-testid="command-palette-input"
        />
        <CommandList data-testid="command-palette-list">
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching the books…
            </div>
          )}
          {!loading && !groups.length && term.trim().length >= 2 && (
            <CommandEmpty data-testid="command-palette-empty">
              Nothing matched “{term.trim()}”.
            </CommandEmpty>
          )}

          {pages.length > 0 && (
            <CommandGroup heading="Go to">
              {pages.map((p) => (
                <CommandItem
                  key={p.to}
                  value={`page ${p.label} ${p.group}`}
                  onSelect={() => go(p.to)}
                  data-testid={`palette-page-${p.id}`}
                  className="gap-2.5"
                >
                  <p.icon className="h-4 w-4 text-brand" />
                  <span>{p.label}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                    {p.group}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {groups.map((g) => (
            <CommandGroup key={g.collection} heading={g.label}>
              {g.items.map((it) => (
                <CommandItem
                  key={`${g.collection}-${it.id}`}
                  value={`${g.collection} ${it.title} ${it.subtitle}`}
                  onSelect={() => go(it.route, it.search)}
                  data-testid={`palette-result-${it.id}`}
                  className="gap-2.5"
                >
                  <span className="truncate">{it.title}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground truncate max-w-[45%]">
                    {it.subtitle}
                  </span>
                  <CornerDownLeft className="h-3 w-3 text-muted-foreground shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
